import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, btnPrimary, btnSecondary } from '../styles/theme.js'
import { projectExport, stats as statsApi, projects as projectsApi } from '../lib/api.js'

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const pageTitle = css`
  font-size: ${font.size['3xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.xs};
`

const pageDesc = css`
  font-size: ${font.size.base};
  color: ${colors.textSecondary};
  margin-bottom: ${space.xl};
  line-height: 1.6;
`

const statsRow = css`
  display: flex;
  gap: ${space.md};
  margin-bottom: ${space.xl};
  flex-wrap: wrap;
`

const statBadge = css`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  padding: ${space.xs} ${space.md};
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  span { font-weight: ${font.weight.semibold}; color: ${colors.text}; }
`

const optionsList = css`
  ${card};
  margin-bottom: ${space.xl};
`

const optionRow = css`
  display: flex;
  align-items: flex-start;
  gap: ${space.md};
  padding: ${space.md} 0;
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const subOptionRow = css`
  ${optionRow};
  padding-left: ${space.xl};
`

const checkbox = css`
  width: 16px;
  height: 16px;
  accent-color: ${colors.brand};
  margin-top: 2px;
  flex-shrink: 0;
  cursor: pointer;
`

const optionLabel = css`
  font-weight: ${font.weight.medium};
  cursor: pointer;
  user-select: none;
`

const optionDesc = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-top: 2px;
`

const actions = css`
  display: flex;
  gap: ${space.md};
  align-items: center;
`

const errorBox = css`
  color: ${colors.critical};
  font-size: ${font.size.sm};
  margin-top: ${space.sm};
`

export function ExportView() {
  let project = null
  let projectStats = null
  let loading = true
  let exporting = false
  let error = null

  // Export options
  const opts = {
    sessions: true,
    messages: false,
    tags: true,
    comments: true,
    evaluations: true,
    auditLog: true,
    maskPii: true,
  }

  async function load(vnode) {
    loading = true
    const projectId = vnode.attrs.projectId
    try {
      const [proj, st] = await Promise.all([
        projectsApi.get(projectId),
        statsApi.get(projectId),
      ])
      project = proj
      projectStats = st
    } catch (err) {
      console.error('Failed to load project:', err)
    } finally {
      loading = false
      m.redraw()
    }
  }

  function triggerDownload(data, filename) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async function doExport(projectId) {
    exporting = true
    error = null
    m.redraw()
    try {
      const params = {}
      for (const [key, val] of Object.entries(opts)) {
        params[key] = val ? '1' : '0'
      }
      const data = await projectExport.generate(projectId, params)
      const safeName = (project?.name || 'project').replace(/[^a-zA-Z0-9_-]/g, '_')
      const date = new Date().toISOString().slice(0, 10)
      triggerDownload(data, `${safeName}_export_${date}.json`)
    } catch (err) {
      console.error('Export failed:', err)
      error = err.message || 'Export failed'
    } finally {
      exporting = false
      m.redraw()
    }
  }

  const OPTIONS = [
    { key: 'sessions', label: 'Sessions', desc: 'Session metadata (ID, timestamp, message count, external ID)' },
    { key: 'messages', label: 'Include full messages', desc: 'All message content with role, text, and sequence', parent: 'sessions' },
    { key: 'maskPii', label: 'Mask PII', desc: 'Replace PII-highlighted text with [REDACTED] in exported messages', parent: 'messages' },
    { key: 'tags', label: 'Tags', desc: 'Tag definitions and which tags are applied to each session' },
    { key: 'comments', label: 'Comments', desc: 'All comments with threading and highlight context' },
    { key: 'evaluations', label: 'Evaluations', desc: 'Evaluation grades, rubric definitions, and criteria' },
    { key: 'auditLog', label: 'Audit log', desc: 'Full audit trail — every action, user, timestamp, and change detail' },
  ]

  return {
    oninit: load,
    view(vnode) {
      const projectId = vnode.attrs.projectId

      if (loading) return m('div', 'Loading...')
      if (!project) return m('div', 'Project not found')

      const s = projectStats || {}

      const rows = []
      for (const opt of OPTIONS) {
        const isChild = !!opt.parent
        const disabled = isChild && !opts[opt.parent]

        rows.push(
          m('div', { class: isChild ? subOptionRow : optionRow }, [
            m('input', {
              class: checkbox,
              type: 'checkbox',
              checked: disabled ? false : opts[opt.key],
              disabled,
              onchange: (e) => {
                opts[opt.key] = e.target.checked
                // If parent unchecked, uncheck children
                if (!e.target.checked) {
                  for (const o of OPTIONS) {
                    if (o.parent === opt.key) opts[o.key] = false
                  }
                }
              },
            }),
            m('div', [
              m('label', {
                class: optionLabel,
                style: disabled ? `color: ${colors.textMuted};` : '',
                onclick: () => {
                  if (disabled) return
                  opts[opt.key] = !opts[opt.key]
                  if (!opts[opt.key]) {
                    for (const o of OPTIONS) {
                      if (o.parent === opt.key) opts[o.key] = false
                    }
                  }
                },
              }, opt.label),
              m('div', { class: optionDesc }, opt.desc),
            ]),
          ])
        )
      }

      const noneSelected = !Object.values(opts).some(Boolean)

      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project.name),
          ' / ',
          'Export',
        ]),

        m('h1', { class: pageTitle }, 'Export project data'),
        m('p', { class: pageDesc }, 'Choose what to include in your JSON export. The file will download automatically.'),

        m('div', { class: statsRow }, [
          m('div', { class: statBadge }, [m('span', s.sessions ?? 0), ' sessions']),
          m('div', { class: statBadge }, [m('span', s.tagged ?? 0), ' tagged']),
          m('div', { class: statBadge }, [m('span', s.evaluated ?? 0), ' evaluated']),
        ]),

        m('div', { class: optionsList }, rows),

        m('div', { class: actions }, [
          m('button', {
            class: btnPrimary,
            disabled: exporting || noneSelected,
            onclick: () => doExport(projectId),
          }, exporting ? 'Exporting...' : 'Export'),
          m(m.route.Link, {
            class: btnSecondary,
            href: `/projects/${projectId}`,
          }, 'Back'),
        ]),

        error ? m('div', { class: errorBox }, error) : null,
      ])
    },
  }
}
